// src/components/user/express/Step4Products.jsx
import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Package, AlertTriangle, ChevronDown, DollarSign } from 'lucide-react';
import axios from 'axios';
import ProductDropdown from './ProductDropdown';

const BASE_URL = process.env.REACT_APP_API_URL || '';

export function Step4Products({ userId, items, setItems, locations, charge, setCharge }) {
  const [inventoryMap, setInventoryMap] = useState({});
  const [refreshFlag, setRefreshFlag]   = useState(0);

  useEffect(() => {
    if (!items.length) return;
    const ids = items.map(i => i.inventory_id).filter(Boolean);
    if (!ids.length) return;

    axios.get(`${BASE_URL}/api/inventory/by-ids`, { params: { ids: ids.join(',') } })
      .then(res => {
        const map = {};
        (res.data || []).forEach(row => { map[row.id] = row.quantity; });
        setInventoryMap(map);
      })
      .catch(() => {});
  }, [items]);

  function addRow() {
    setItems(prev => [...prev, { sku: '', title: '', quantity: 1, inventory_id: null, location_name: '' }]);
  }

  function removeRow(idx) {
    setItems(prev => prev.filter((_, i) => i !== idx));
  }

  function updateRow(idx, patch) {
    setItems(prev => prev.map((r, i) => i === idx ? { ...r, ...patch } : r));
  }

  function getStockWarning(item) {
    if (!item.inventory_id) return null;
    const stock = inventoryMap[item.inventory_id];
    if (stock === undefined) return null;
    if (stock <= 0) return `Sin stock en esta ubicación`;
    if (item.quantity > stock) return `Stock disponible: ${stock} unidad${stock !== 1 ? 'es' : ''}`;
    return null;
  }

  // Handle charge input — allow empty string while typing
  function handleChargeChange(e) {
    const raw = e.target.value;
    // Allow empty or valid positive number
    if (raw === '' || raw === '.') {
      setCharge('');
      return;
    }
    const parsed = parseFloat(raw);
    if (!isNaN(parsed) && parsed >= 0) {
      setCharge(raw); // keep as string while editing
    }
  }

  function handleChargeBlur(e) {
    const parsed = parseFloat(e.target.value);
    setCharge(isNaN(parsed) || parsed <= 0 ? '' : parsed.toFixed(2));
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-extrabold text-slate-800 font-montserrat mb-1">
          Productos a enviar
        </h2>
        <p className="text-sm text-slate-500 font-montserrat">
          Agrega los productos, selecciona la ubicación de inventario y el monto a cobrar si aplica
        </p>
      </div>

      {/* ── Charge field ───────────────────────────────────── */}
      <div className="bg-white rounded-xl border-2 border-slate-100 p-4 space-y-2">
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-brand-gold/10 rounded-lg">
            <DollarSign className="w-4 h-4 text-brand-gold" />
          </div>
          <span className="text-sm font-bold text-slate-700 font-montserrat">
            Monto a cobrar al destinatario
          </span>
          <span className="text-xs text-slate-400 font-montserrat ml-auto">
            Opcional
          </span>
        </div>

        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-slate-400 font-montserrat">
            S/
          </span>
          <input
            type="number"
            min="0"
            step="0.01"
            placeholder="0.00"
            value={charge}
            onChange={handleChargeChange}
            onBlur={handleChargeBlur}
            className="w-full rounded-lg border-2 border-slate-200 bg-slate-50 pl-9 pr-3 py-2.5 text-sm font-montserrat focus:outline-none focus:border-brand-primary transition-colors"
          />
        </div>

        <p className="text-xs text-slate-400 font-montserrat">
          Si el destinatario debe pagar un monto, ingrésalo aquí. El conductor lo registrará al momento de la entrega.
        </p>
      </div>

      {/* ── Product rows ───────────────────────────────────── */}
      <div className="space-y-3">
        {items.map((item, idx) => {
          const warning = getStockWarning(item);
          return (
            <div
              key={idx}
              className="bg-slate-50 rounded-xl p-4 border-2 border-slate-100 space-y-3"
            >
              {/* Header row */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-brand-primary/10 rounded-lg">
                    <Package className="w-4 h-4 text-brand-primary" />
                  </div>
                  <span className="text-xs font-bold text-slate-600 font-montserrat">
                    Producto {idx + 1}
                  </span>
                </div>
                <button
                  onClick={() => removeRow(idx)}
                  className="p-1.5 hover:bg-red-50 rounded-lg transition-colors text-red-400"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              {/* Product selector */}
              <ProductDropdown
                userId={userId}
                refreshFlag={refreshFlag}
                value={item.sku ? { sku: item.sku, title: item.title } : null}
                onSelect={prod => {
                  if (prod) updateRow(idx, { sku: prod.sku, title: prod.title, inventory_id: null });
                  else updateRow(idx, { sku: '', title: '', inventory_id: null });
                }}
              />

              {/* Quantity + location row */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-semibold text-slate-500 font-montserrat mb-1">
                    Cantidad
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={item.quantity}
                    onChange={e => updateRow(idx, { quantity: Math.max(1, parseInt(e.target.value) || 1) })}
                    className="w-full rounded-lg border-2 border-slate-200 bg-white px-3 py-2 text-sm font-montserrat focus:outline-none focus:border-brand-primary transition-colors text-center"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-500 font-montserrat mb-1">
                    Ubicación
                  </label>
                  <div className="relative">
                    <select
                      value={item.inventory_id || ''}
                      onChange={e => {
                        const loc = locations.find(l => l.id === parseInt(e.target.value));
                        updateRow(idx, {
                          inventory_id:  parseInt(e.target.value) || null,
                          location_name: loc?.name || '',
                        });
                      }}
                      className="w-full appearance-none rounded-lg border-2 border-slate-200 bg-white px-3 py-2 text-sm font-montserrat focus:outline-none focus:border-brand-primary transition-colors pr-7"
                    >
                      <option value="">Elegir…</option>
                      {locations.map(loc => (
                        <option key={loc.id} value={loc.id}>
                          {loc.name}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                  </div>
                </div>
              </div>

              {/* Stock warning */}
              {warning && (
                <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                  <p className="text-xs text-amber-700 font-montserrat">{warning}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Add product button */}
      <button
        onClick={addRow}
        className="w-full py-3 rounded-xl border-2 border-dashed border-slate-200 hover:border-brand-primary hover:bg-brand-primary/5 transition-all flex items-center justify-center gap-2 text-sm font-montserrat font-semibold text-slate-500 hover:text-brand-primary"
      >
        <Plus className="w-4 h-4" />
        Agregar producto
      </button>
    </div>
  );
}